import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { processScheduleService } from '../services/workspaceAPI';
import { OkAction, ErrorAction, Editing } from './baseActions';
import {
  ProcessSchedule,
  WeekdaysProcessSchedule,
  CronProcessSchedule,
  IntervalProcessSchedule,
} from '../types/resources';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

type AppState = import('~/store').AppState;

export enum ProcessScheduleActionType {
  SILENT_GET_PROCESS_SCHEDULES = 'SILENT_GET_PROCESS_SCHEDULES',
  GET_PROCESS_SCHEDULES = 'GET_PROCESS_SCHEDULES',
  GET_PROCESS_SCHEDULES_OK = 'GET_PROCESS_SCHEDULES_OK',
  GET_PROCESS_SCHEDULES_ERROR = 'GET_PROCESS_SCHEDULES_ERROR',
  CREATE_PROCESS_SCHEDULE = 'CREATE_PROCESS_SCHEDULE',
  CREATE_PROCESS_SCHEDULE_OK = 'CREATE_PROCESS_SCHEDULE_OK',
  CREATE_PROCESS_SCHEDULE_ERROR = 'CREATE_PROCESS_SCHEDULE_ERROR',
  DELETE_PROCESS_SCHEDULE = 'DELETE_PROCESS_SCHEDULE',
  DELETE_PROCESS_SCHEDULE_OK = 'DELETE_PROCESS_SCHEDULE_OK',
  DELETE_PROCESS_SCHEDULE_ERROR = 'DELETE_PROCESS_SCHEDULE_ERROR',
  EDIT_PROCESS_SCHEDULE = 'EDIT_PROCESS_SCHEDULE',
  EDIT_PROCESS_SCHEDULE_OK = 'EDIT_PROCESS_SCHEDULE_OK',
  EDIT_PROCESS_SCHEDULE_ERROR = 'EDIT_PROCESS_SCHEDULE_ERROR',
  SILENT_LIST_WORKSPACE_SCHEDULES = 'SILENT_LIST_WORKSPACE_SCHEDULES',
  LIST_WORKSPACE_SCHEDULES = 'LIST_WORKSPACE_SCHEDULES',
  LIST_WORKSPACE_SCHEDULES_OK = 'LIST_WORKSPACE_SCHEDULES_OK',
  LIST_WORKSPACE_SCHEDULES_ERROR = 'LIST_WORKSPACE_SCHEDULES_ERROR',
}

type WithScheduleId = { scheduleId: string };
type WithProcessId = { processId: string };

export type ProcessScheduleEditingDetails =
  | Editing<'weekdayEditing', 'editWeekdayError'>
  | Editing<'changingInterval', 'editIntervalError'>
  | Editing<'cronEditing', 'editCronError'>;

type GetProcessSchedulesAction =
  | Action<ProcessScheduleActionType.SILENT_GET_PROCESS_SCHEDULES>
  | Action<ProcessScheduleActionType.GET_PROCESS_SCHEDULES>
  | (OkAction<ProcessScheduleActionType.GET_PROCESS_SCHEDULES_OK, ProcessSchedule[]> &
      WithProcessId)
  | ErrorAction<ProcessScheduleActionType.GET_PROCESS_SCHEDULES_ERROR>;

type ListWorkspaceSchedulesAction =
  | Action<ProcessScheduleActionType.SILENT_LIST_WORKSPACE_SCHEDULES>
  | Action<ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES>
  | OkAction<ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES_OK, ProcessSchedule[]>
  | ErrorAction<ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES_ERROR>;

type CreateProcessScheduleAction =
  | Action<ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE>
  | OkAction<ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE_OK, ProcessSchedule>
  | ErrorAction<ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE_ERROR>;

type DeleteProcessScheduleAction =
  | (Action<ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE> & WithScheduleId)
  | (OkAction<ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE_OK, number> & WithScheduleId)
  | (ErrorAction<ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE_ERROR> & WithScheduleId);

type EditProcessScheduleAction =
  | (Action<ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE> &
      WithScheduleId &
      ProcessScheduleEditingDetails)
  | (OkAction<ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE_OK, ProcessSchedule> &
      WithScheduleId &
      ProcessScheduleEditingDetails)
  | (ErrorAction<ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE_ERROR> &
      WithScheduleId &
      ProcessScheduleEditingDetails);

export type ProcessScheduleAction =
  | GetProcessSchedulesAction
  | CreateProcessScheduleAction
  | DeleteProcessScheduleAction
  | EditProcessScheduleAction
  | ListWorkspaceSchedulesAction;

export const getProcessSchedulesById =
  (processId: string): ThunkAction<void, AppState, null, GetProcessSchedulesAction> =>
  async (dispatch, getState) => {
    const schedules = Object.values(getState().workspaceResources.processSchedules.byId);

    if (
      schedules.length > 0 &&
      schedules.every((schedule) => schedule.target.targetId.toLowerCase() === processId)
    ) {
      dispatch({
        type: ProcessScheduleActionType.SILENT_GET_PROCESS_SCHEDULES,
      });
    } else {
      dispatch({ type: ProcessScheduleActionType.GET_PROCESS_SCHEDULES });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processScheduleService.getSchedulesByProcessId({
      workspaceId,
      processId,
    });
    if (response.ok) {
      dispatch({
        type: ProcessScheduleActionType.GET_PROCESS_SCHEDULES_OK,
        data: response.data as ProcessSchedule[],
        processId,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessScheduleActionType.GET_PROCESS_SCHEDULES_ERROR,
      });
    }
  };

export const listWorkspaceSchedules =
  (silent = false): ThunkAction<void, AppState, null, ListWorkspaceSchedulesAction> =>
  async (dispatch, getState) => {
    if (silent || (getState().workspaceResources.processSchedules.allIds as string[]).length > 0) {
      dispatch({
        type: ProcessScheduleActionType.SILENT_LIST_WORKSPACE_SCHEDULES,
      });
    } else {
      dispatch({ type: ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processScheduleService.listWorkspaceSchedules({ workspaceId });
    if (response.ok) {
      dispatch({
        type: ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES_OK,
        data: response.data as ProcessSchedule[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessScheduleActionType.LIST_WORKSPACE_SCHEDULES_ERROR,
      });
    }
  };

export const createProcessSchedule =
  (
    robotId: string,
    schedule: ProcessSchedule,
  ): ThunkAction<void, AppState, null, CreateProcessScheduleAction> =>
  async (dispatch, getState) => {
    dispatch({ type: ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processScheduleService.createSchedule({
      workspaceId,
      processId: robotId,
      schedule: {
        ...schedule,
        id: undefined,
      },
    });
    if (response.ok) {
      dispatch({
        type: ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE_OK,
        data: response.data as ProcessSchedule,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessScheduleActionType.CREATE_PROCESS_SCHEDULE_ERROR,
      });
    }
  };

export const deleteProcessSchedule =
  (
    processId: string,
    scheduleId: string,
  ): ThunkAction<void, AppState, null, DeleteProcessScheduleAction> =>
  async (dispatch, getState) => {
    dispatch({
      type: ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE,
      scheduleId,
    });
    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processScheduleService.deleteRobotSchedule({
      workspaceId,
      processId,
      scheduleId,
    });
    if (response.ok) {
      dispatch(getProcessSchedulesById(processId));
      dispatch({
        scheduleId,
        type: ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE_OK,
        data: response.data as number,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessScheduleActionType.DELETE_PROCESS_SCHEDULE_ERROR,
        scheduleId,
      });
    }
  };

const editProcessSchedule =
  (
    processId: string,
    scheduleId: string,
    update: Partial<ProcessSchedule>,
    editingDetails: ProcessScheduleEditingDetails,
  ): ThunkAction<void, AppState, null, EditProcessScheduleAction> =>
  async (dispatch, getState) => {
    const schedule = getState().workspaceResources.processSchedules.byId[scheduleId];
    const updated = { ...schedule, ...update } as ProcessSchedule;
    dispatch({
      type: ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE,
      scheduleId,
      ...editingDetails,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processScheduleService.editRobotSchedule({
      workspaceId,
      processId,
      scheduleId,
      schedule: updated,
    });
    if (response.ok) {
      dispatch({
        scheduleId,
        ...editingDetails,
        type: ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE_OK,
        data: response.data as ProcessSchedule,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        ...editingDetails,
        type: ProcessScheduleActionType.EDIT_PROCESS_SCHEDULE_ERROR,
        scheduleId,
      });
    }
  };

export const editIntervalSchedule = (
  robotId: string,
  scheduleId: string,
  schedule: IntervalProcessSchedule,
) =>
  editProcessSchedule(robotId, scheduleId, schedule, {
    statusKey: 'changingInterval',
    errorKey: 'editIntervalError',
  });

export const editWeekdaySchedule = (
  robotId: string,
  scheduleId: string,
  schedule: WeekdaysProcessSchedule,
) =>
  editProcessSchedule(robotId, scheduleId, schedule, {
    statusKey: 'weekdayEditing',
    errorKey: 'editWeekdayError',
  });

export const editCronSchedule = (
  processId: string,
  scheduleId: string,
  schedule: CronProcessSchedule,
) =>
  editProcessSchedule(processId, scheduleId, schedule, {
    statusKey: 'cronEditing',
    errorKey: 'editCronError',
  });
